import { useState, useEffect } from 'react';
import { useAuthFetch } from '@/hooks/useAdmin';
import { Upload, X, Loader2, Link2, Image as ImageIcon, Library, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Media picker with three sources:
 *  1. "library"   – Global media library (admin uploads, AI-generated, etc.)
 *  2. "post"      – Images already attached to the article being edited
 *                   (gallery + cover, when not already used as the cover).
 *                   Only visible when `postGalleryUrls` is non-empty.
 *  3. "url"       – Paste any external image URL.
 *
 * Multi-select (Feb 2026): when `multiple` is true, library + post tabs become
 * checkbox grids and the picker only calls `onSelect` once the user clicks
 * "Dodaj odabrane" — the callback receives an *array* of URLs. The file input
 * also accepts multiple files in this mode. Single-select callers receive a
 * string URL (unchanged behaviour).
 */
export default function MediaPickerModal({ onSelect, onClose, postGalleryUrls = [], multiple = false }) {
  const authFetch = useAuthFetch();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const hasPostGallery = Array.isArray(postGalleryUrls) && postGalleryUrls.filter(Boolean).length > 0;
  const [tab, setTab] = useState(hasPostGallery ? 'post' : 'library');
  const [externalUrl, setExternalUrl] = useState('');
  const [selected, setSelected] = useState([]); // URLs queued in multi mode

  useEffect(() => { loadMedia(); }, []);

  async function loadMedia() {
    try {
      const res = await authFetch(`${API}/admin/media?limit=100`);
      if (res.ok) { const data = await res.json(); setMedia(data.media || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  function emit(value) {
    // Always invoke with the shape the caller expects: array in multi, string in single.
    onSelect(multiple ? (Array.isArray(value) ? value : [value]) : value);
  }

  function togglePick(url) {
    if (!multiple) { emit(url); return; }
    setSelected(curr => curr.includes(url) ? curr.filter(u => u !== url) : [...curr, url]);
  }

  async function handleUpload(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    const uploadedUrls = [];
    for (let i = 0; i < files.length; i++) {
      try {
        const formData = new FormData();
        formData.append('file', files[i]);
        const res = await authFetch(`${API}/admin/media/upload`, { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          if (data.url) uploadedUrls.push(data.url);
        }
      } catch (err) { console.error(err); }
      setUploadProgress({ done: i + 1, total: files.length });
    }
    setUploading(false);
    e.target.value = ''; // allow re-uploading the same files
    if (uploadedUrls.length === 0) return;
    if (multiple) {
      // Stage uploaded URLs into the selection so the user can review and
      // confirm with the "Dodaj odabrane" button. Refresh the library grid
      // so the new images appear there too.
      setSelected(curr => [...curr, ...uploadedUrls]);
      loadMedia();
    } else {
      // Single-select: emit the first upload immediately (preserves prior UX).
      emit(uploadedUrls[0]);
    }
  }

  function fixImg(url) {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND}${url}`;
  }

  function submitExternalUrl(e) {
    e?.preventDefault?.();
    const u = externalUrl.trim();
    if (!u) return;
    // Accept http(s) or absolute /api/uploads paths.
    if (!/^(https?:\/\/|\/)/i.test(u)) return;
    if (multiple) {
      setSelected(curr => curr.includes(u) ? curr : [...curr, u]);
      setExternalUrl('');
    } else {
      emit(u);
    }
  }

  function confirmSelection() {
    if (selected.length === 0) return;
    emit(selected);
  }

  const TabBtn = ({ id, label, icon: Icon, badge }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors border ${
        tab === id
          ? 'bg-zinc-700 text-white border-zinc-600'
          : 'bg-transparent text-zinc-400 hover:text-white border-transparent'
      }`}
      data-testid={`media-tab-${id}`}
    >
      <Icon size={13} /> {label}
      {typeof badge === 'number' && badge > 0 && (
        <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-600 text-white">{badge}</span>
      )}
    </button>
  );

  function PickThumb({ url, label, testId }) {
    const isSelected = multiple && selected.includes(url);
    return (
      <button
        type="button"
        onClick={() => togglePick(url)}
        data-testid={testId}
        title={url}
        className={`relative aspect-square rounded bg-zinc-800 overflow-hidden hover:ring-2 ring-blue-500 transition-all ${isSelected ? 'ring-2 ring-emerald-500' : ''}`}
      >
        <img src={fixImg(url)} alt="" className="w-full h-full object-cover" />
        {multiple && (
          <span className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center border ${isSelected ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-black/60 border-white/40 text-transparent'}`}>
            <Check size={12} />
          </span>
        )}
        {label && (
          <span className="absolute bottom-0 left-0 right-0 text-[9px] py-0.5 bg-black/70 text-white truncate px-1">{label}</span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="media-picker-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-zinc-700">
          <h2 className="text-sm font-medium text-zinc-200">
            Odaberi medij{multiple ? ' (više datoteka)' : ''}
          </h2>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded cursor-pointer" data-testid="media-upload-btn">
              <Upload size={14} />
              {uploading
                ? `Prijenos ${uploadProgress.done}/${uploadProgress.total}...`
                : (multiple ? 'Prenesi (više)' : 'Prenesi')}
              <input type="file" accept="image/*" multiple={multiple} onChange={handleUpload} className="hidden" />
            </label>
            <button onClick={onClose} className="p-1 text-zinc-500 hover:text-white" data-testid="media-close-btn"><X size={18} /></button>
          </div>
        </div>

        <div className="flex items-center gap-1.5 px-4 pt-3 border-b border-zinc-800 pb-2">
          {hasPostGallery && (
            <TabBtn id="post" label="Iz ovog članka" icon={ImageIcon}
              badge={postGalleryUrls.filter(Boolean).length} />
          )}
          <TabBtn id="library" label="Biblioteka" icon={Library} />
          <TabBtn id="url" label="URL" icon={Link2} />
        </div>

        <div className="flex-1 overflow-auto p-4">
          {tab === 'post' && hasPostGallery && (
            <div className="grid grid-cols-4 gap-2" data-testid="media-tab-post-grid">
              {postGalleryUrls.filter(Boolean).map((url, i) => (
                <PickThumb key={`pg-${i}`} url={url} label={`Galerija #${i + 1}`} testId={`media-post-item-${i}`} />
              ))}
            </div>
          )}

          {tab === 'library' && (
            loading ? (
              <div className="flex justify-center py-8"><Loader2 className="animate-spin text-zinc-500" /></div>
            ) : media.length === 0 ? (
              <p className="text-center text-zinc-500 py-8">Nema medija. Prenesite sliku.</p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {media.map(m => (
                  <PickThumb key={m.id} url={m.url} testId={`media-item-${m.id}`} />
                ))}
              </div>
            )
          )}

          {tab === 'url' && (
            <form onSubmit={submitExternalUrl} className="space-y-3" data-testid="media-tab-url">
              <label className="block text-xs text-zinc-500">Unesite URL slike (https:// ili /api/uploads/…)</label>
              <input
                type="url"
                value={externalUrl}
                onChange={e => setExternalUrl(e.target.value)}
                placeholder="https://primjer.hr/slika.jpg"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none focus:border-blue-500"
                data-testid="media-url-input"
                autoFocus
              />
              {externalUrl && /^(https?:\/\/|\/)/i.test(externalUrl) && (
                <div className="rounded-md bg-zinc-800 border border-zinc-700 p-2">
                  <p className="text-[10px] text-zinc-500 mb-1">Pregled</p>
                  <img
                    src={fixImg(externalUrl)}
                    alt="preview"
                    className="max-h-40 mx-auto rounded"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white">Odustani</button>
                <button
                  type="submit"
                  disabled={!externalUrl.trim() || !/^(https?:\/\/|\/)/i.test(externalUrl.trim())}
                  className="px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded"
                  data-testid="media-url-submit"
                >
                  {multiple ? 'Dodaj URL u odabir' : 'Koristi ovaj URL'}
                </button>
              </div>
            </form>
          )}
        </div>

        {multiple && (
          <div className="flex items-center justify-between gap-3 p-3 border-t border-zinc-800 bg-zinc-950/50">
            <span className="text-xs text-zinc-400" data-testid="media-multi-count">
              Odabrano: <strong className="text-white">{selected.length}</strong>
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelected([])}
                disabled={selected.length === 0}
                className="px-3 py-1.5 text-xs text-zinc-400 hover:text-white disabled:opacity-40"
                data-testid="media-multi-clear"
              >
                Poništi
              </button>
              <button
                type="button"
                onClick={confirmSelection}
                disabled={selected.length === 0}
                className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded font-semibold"
                data-testid="media-multi-confirm"
              >
                <Check size={14} /> Dodaj odabrane ({selected.length})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
